import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';
import {
  isLowPowerDevice,
  prefersReducedMotion,
  shouldDisableCrossDocumentViewTransitions,
  supportsViewTransitions,
  viewTransition,
} from '@theme/utilities';

const ROW = 'tr[data-key][ref="cartItemRows[]"]';
const REMOVE = 'button.cart-items__remove[type="button"]';
const EXCLUDED = '#cart-drawer, theme-drawer, dialog';
const INPUT = 'cart-quantity-selector-component input[type="number"][name="updates[]"][ref="quantityInput"]';
const pointerButtons = new WeakSet();
const requests = new Set();
const seenMutations = new WeakSet();
// Normally one latest document-delivered intent. A disconnected owner no longer
// receives superseding document events, so its retained debounce needs its own watch.
const quantityWatches = new Map();
let quantityGeneration = 0;
let removeClick = null;
let activation = null;
let current = null;
let generation = 0;
let uncertainMutation = false;

function removeButton(target) {
  return target instanceof Element ? target.closest(REMOVE) : null;
}

function ownedRows(owner) {
  return [...owner.querySelectorAll(ROW)].filter((row) => row.closest('cart-items-component') === owner);
}

function validProductRows(owner, page, rows, empty) {
  const owned = (selector) => [...owner.querySelectorAll(selector)].filter(
    (node) => node.closest('cart-items-component') === owner
  );
  const identity = 'tr[data-key], tr[data-parent-key], tr[ref="cartItemRows[]"], tr.cart-items__nested-line, tr[id^="CartItem-"]';
  const marked = owned(`${identity}, tr.cart-items__table-row`);
  const tables = owned('table.cart-items__table');
  if (empty) return !rows.length && !marked.length && !tables.length && !owner.querySelector('form.cart-form');
  if (tables.length !== 1 || tables[0].closest('.cart-page') !== page) return false;
  const table = tables[0];
  // Horizon shares the row styling class with its THEAD. Exempt only a direct
  // header row of this table with NO product identity; misplaced product markers
  // (including a canonical CartItem-* id) must still invalidate recovery.
  const productRows = marked.filter((row) => !(row.parentElement?.matches('thead') &&
    row.parentElement.parentElement === table && !row.matches(identity)));
  // Inspect ALL body rows, not just canonical refs. Header rows never enter this
  // list. Unexpected rows invalidate recovery; they are never focus destinations.
  const bodyRows = [...table.querySelectorAll('tbody tr')].filter((row) => row.closest('table') === table);
  if (bodyRows.length !== rows.length || productRows.length !== rows.length ||
      bodyRows.some((row, index) => row !== rows[index] || !row.matches(ROW) ||
        !row.parentElement?.matches('tbody') || row.parentElement.parentElement !== table) ||
      productRows.some((row) => !rows.includes(row))) return false;
  const keys = rows.map((row) => row.dataset.key);
  if (keys.some((key) => !key) || new Set(keys).size !== keys.length) return false;
  return rows.every((row, index) => {
    const parent = row.dataset.parentKey || '';
    if (row.classList.contains('cart-items__nested-line') !== Boolean(parent)) return false;
    if (!parent) return true;
    const parentIndex = keys.indexOf(parent);
    return parentIndex >= 0 && parentIndex < index && !rows[parentIndex].dataset.parentKey;
  });
}

function retireQuantity(intent) {
  if (quantityWatches.get(intent.owner) !== intent) return;
  quantityWatches.delete(intent.owner);
  intent.listeners.abort();
}

function hasPendingQuantity() {
  for (const intent of quantityWatches.values()) {
    // This is core's skip guard. isConnected is deliberately irrelevant: an
    // entire detached owner can still contain its source and issue a request.
    if (!intent.owner.contains(intent.source)) retireQuantity(intent);
  }
  return quantityWatches.size > 0;
}

function consumeQuantity(event) {
  hasPendingQuantity();
  if (!(event instanceof CartLinesUpdateEvent)) return;
  const intent = quantityWatches.get(event.target);
  if (!intent || event.context !== 'cart' || event.lines?.length !== 1) return;
  // A native remove click is a separate operation, even if a zero-quantity
  // debounce is also pending. Do not consume that opportunity on its behalf.
  if (removeClick && removeClick.eventPhase !== Event.NONE) return;
  // Core reads cached refs at dispatch time. Live row indices/keys can already
  // differ following optimistic removal; compare with the SAME read as core.
  const key = intent.owner.refs?.cartItemRows?.[intent.line - 1]?.dataset.key ?? '';
  const line = event.lines[0];
  if (event.action === (intent.quantity > 0 ? 'update' : 'remove') && line.id === key &&
      Object.is(line.quantity, intent.quantity)) retireQuantity(intent);
}

function cartPage(owner) {
  if (!(owner instanceof HTMLElement) || !owner.isConnected || owner.hasAttribute('data-drawer')) return null;
  if (!owner.matches('cart-items-component') || owner.closest(EXCLUDED)) return null;
  const pages = [...owner.querySelectorAll('.cart-page')].filter(
    (page) => page.closest('cart-items-component') === owner && !page.closest(EXCLUDED)
  );
  return pages.length === 1 ? pages[0] : null;
}

function eligible(target, owner) {
  return (
    target instanceof HTMLElement && target.isConnected &&
    target.closest('cart-items-component') === owner &&
    target.closest('.cart-page') === cartPage(owner) && !target.closest(EXCLUDED) &&
    target.tabIndex >= 0 && !target.matches(':disabled') &&
    !target.closest('[inert], [hidden], [aria-hidden="true"], [aria-disabled="true"]') &&
    getComputedStyle(target).visibility === 'visible' && target.getClientRects().length > 0
  );
}

function synchronousEmptyFallback() {
  // These are precisely core startViewTransition's synchronous branches.
  return !supportsViewTransitions() || isLowPowerDevice() || prefersReducedMotion() ||
    shouldDisableCrossDocumentViewTransitions();
}

function isCurrent(record) {
  return current === record && !record.abandoned;
}

function abandon(record = current) {
  if (!record || !isCurrent(record)) return;
  record.abandoned = true;
  record.listeners.abort();
  current = null;
}

function contextValid(record) {
  return isCurrent(record) && location.href === record.url && document.visibilityState === 'visible' &&
    document.hasFocus() && cartPage(record.owner) && record.owner.dataset.sectionId === record.sectionId &&
    record.section.isConnected && record.owner.closest('.shopify-section') === record.section &&
    document.getElementById(record.section.id) === record.section;
}

function watchIntent(record) {
  const options = { capture: true, signal: record.listeners.signal };
  const cancel = () => abandon(record);
  window.addEventListener('focusin', (event) => {
    if (event.target !== record.button) cancel();
  }, options);
  // The activation's keydown has already happened; a subsequent key is new intent.
  window.addEventListener('keydown', cancel, options);
  window.addEventListener('pointerdown', cancel, options);
  window.addEventListener('pagehide', cancel, options);
  window.addEventListener('popstate', cancel, options);
  window.addEventListener('hashchange', cancel, options);
  window.addEventListener('blur', (event) => {
    if (event.target === window) cancel();
  }, options);
  document.addEventListener('visibilitychange', cancel, options);
  window.navigation?.addEventListener('navigate', cancel, options);
}

function rememberActivation(event) {
  hasPendingQuantity();
  const wasPending = Boolean(current);
  abandon();
  activation = null;
  const button = removeButton(event.target);
  removeClick = button ? event : null;
  if (!button || wasPending || requests.size || quantityWatches.size || uncertainMutation) return;
  // detail=0 includes native assistive-technology activation, not just keyboards.
  // A pointerdown veto survives until a fresh trusted Enter/Space on this button.
  if (!event.isTrusted || event.detail !== 0 || event.pointerType || pointerButtons.has(button)) return;
  if (event.defaultPrevented || document.activeElement !== button) return;
  const owner = button.closest('cart-items-component');
  const page = cartPage(owner);
  const row = button.closest(ROW);
  const section = owner?.closest('.shopify-section');
  if (!page || !row || row.closest('.cart-page') !== page || !eligible(button, owner)) return;
  if (!section?.id || !owner.dataset.sectionId || section.id !== `shopify-section-${owner.dataset.sectionId}`) return;
  const rows = ownedRows(owner);
  const keys = rows.map((item) => item.dataset.key);
  if (keys.some((key) => !key) || new Set(keys).size !== keys.length) return;
  const index = rows.indexOf(row);
  if (index < 0 || button.getAttribute('on:click') !== `/onLineItemRemove/${index + 1}`) return;
  const affected = rows.filter((item) => item === row || item.dataset.parentKey === row.dataset.key);
  const topKeys = rows.filter((item) => !item.dataset.parentKey).map((item) => item.dataset.key);
  // Nested rows never become independent destinations or ambiguous origins.
  const position = topKeys.indexOf(row.dataset.key);
  if (position < 0) return;
  activation = {
    generation: ++generation, click: event, button, owner, row, section,
    sectionId: owner.dataset.sectionId, key: row.dataset.key, topKeys, position,
    affectedKeys: affected.map((item) => item.dataset.key),
    remaining: rows.filter((item) => !affected.includes(item)).map((item) => ({
      key: item.dataset.key, parent: item.dataset.parentKey || '',
    })),
    empty: affected.length === rows.length, url: location.href,
    provenance: 'trusted-native-non-pointer', transitionBefore: viewTransition.current,
    synchronousFallback: synchronousEmptyFallback(), request: null, abandoned: false,
  };
}

function validResponseSection(record, html, items) {
  // Parse an inert response document; optimistic live markup is not evidence that
  // core's unawaited morph received a usable section. Never insert these nodes.
  const response = new DOMParser().parseFromString(html, 'text/html');
  const sections = response.querySelectorAll('.shopify-section');
  const section = response.getElementById(record.section.id);
  if (sections.length !== 1 || sections[0] !== section || section.parentElement !== response.body ||
      section.tagName !== record.section.tagName ||
      [...response.querySelectorAll('[id]')].filter((node) => node.id === record.section.id).length !== 1) return false;

  const owners = response.querySelectorAll('cart-items-component');
  const owner = owners[0];
  if (owners.length !== 1 || owner.dataset.sectionId !== record.sectionId || owner.hasAttribute('data-drawer') ||
      owner.closest('.shopify-section') !== section || owner.closest(EXCLUDED)) return false;
  const pages = response.querySelectorAll('.cart-page');
  const page = pages[0];
  if (pages.length !== 1 || page.closest('cart-items-component') !== owner || page.closest(EXCLUDED) ||
      page.classList.contains('cart-page--empty') !== record.empty) return false;
  const containers = page.querySelectorAll('.cart-page__items');
  const container = containers[0];
  if (containers.length !== 1 || container.closest('.cart-page') !== page) return false;

  const rows = ownedRows(owner);
  if (!validProductRows(owner, page, rows, record.empty)) return false;
  if (rows.length !== items.length || rows.some((row, index) =>
    row.dataset.key !== items[index].key || row.closest('.cart-page') !== page || !container.contains(row) ||
    record.remaining.find((item) => item.key === row.dataset.key)?.parent !== (row.dataset.parentKey || '')
  )) return false;
  if (record.affectedKeys.some((key) => rows.some((row) => row.dataset.key === key))) return false;

  if (record.empty) {
    if (owner.querySelector('tr[data-key], .cart-items__table-row, table.cart-items__table, form.cart-form')) return false;
    const links = page.querySelectorAll('a.cart-items__empty-button[href]');
    const link = links[0];
    return links.length === 1 && container.contains(link) && link.closest('cart-items-component') === owner &&
      Boolean(link.getAttribute('href')?.trim()) && link.tabIndex >= 0 && !link.matches(':disabled') &&
      !link.closest(`${EXCLUDED}, [inert], [hidden], [aria-hidden="true"], [aria-disabled="true"]`);
  }

  if (page.querySelector('.cart-items__empty-button')) return false;
  const forms = container.querySelectorAll('form.cart-form');
  const tables = container.querySelectorAll('table.cart-items__table');
  const form = forms[0];
  const table = tables[0];
  const counts = container.querySelectorAll('[ref="cartItemCount"]');
  return forms.length === 1 && tables.length === 1 && table.closest('form.cart-form') === form &&
    form.closest('cart-items-component') === owner && counts.length === 1 &&
    counts[0].textContent.trim() === String(items.reduce((sum, item) => sum + item.quantity, 0)) &&
    rows.every((row) => row.closest('form.cart-form') === form && row.closest('table') === table &&
      row.parentElement?.matches('tbody') && row.parentElement.parentElement === table);
}

function validResult(record, result) {
  const detail = result?.detail;
  const items = detail?.items;
  if (result?.userErrors?.length || detail?.didError !== false || detail?.source !== 'cart-items-component') return false;
  if (typeof detail?.sections?.[record.sectionId] !== 'string' || !detail.sections[record.sectionId].trim()) return false;
  if (!Array.isArray(items) || items.length !== record.remaining.length) return false;
  const keys = new Set(items.map((item) => item.key));
  if (keys.size !== items.length || items.some((item, index) => item.key !== record.remaining[index].key)) return false;
  if (record.affectedKeys.some((key) => keys.has(key))) return false;
  if (items.some((item) => !Number.isInteger(item.quantity) || item.quantity <= 0)) return false;
  const count = items.reduce((sum, item) => sum + item.quantity, 0);
  return result.cart?.totalQuantity === count && detail.itemCount === count && (count === 0) === record.empty &&
    validResponseSection(record, detail.sections[record.sectionId], items);
}

function recover(record, result) {
  if (!isCurrent(record)) return;
  if (!contextValid(record) || requests.size || hasPendingQuantity() || uncertainMutation || viewTransition.current) {
    abandon(record);
    return;
  }
  const active = document.activeElement;
  if (active !== document.body && active !== document.documentElement && active !== record.button) {
    abandon(record);
    return;
  }
  const page = cartPage(record.owner);
  const rows = ownedRows(record.owner);
  if (!validProductRows(record.owner, page, rows, record.empty)) {
    abandon(record);
    return;
  }
  const responseKeys = result.detail.items.map((item) => item.key);
  const liveKeys = rows.map((row) => row.dataset.key);
  if (liveKeys.length !== responseKeys.length || liveKeys.some((key, index) => key !== responseKeys[index]) ||
      record.affectedKeys.some((key) => liveKeys.includes(key)) ||
      rows.some((row) => row.closest('.cart-page') !== page ||
        record.remaining.find((item) => item.key === row.dataset.key)?.parent !== (row.dataset.parentKey || '')) ||
      page.classList.contains('cart-page--empty') !== record.empty) {
    abandon(record);
    return;
  }
  let target;
  if (record.empty) {
    target = page.querySelector('a.cart-items__empty-button[href]');
  } else {
    const candidates = [
      ...record.topKeys.slice(record.position + 1),
      ...record.topKeys.slice(0, record.position).reverse(),
    ];
    const key = candidates.find((candidate) => rows.some((row) => row.dataset.key === candidate && !row.dataset.parentKey));
    const row = rows.find((item) => item.dataset.key === key);
    // Only explicit safe destinations in the selected row, never a remove button.
    for (const selector of ['a.cart-items__title[href]', 'a.cart-items__media-container[href]', INPUT]) {
      const candidate = row?.querySelector(selector);
      if (candidate?.closest(ROW) === row && eligible(candidate, record.owner)) {
        target = candidate;
        break;
      }
    }
  }
  if (!eligible(target, record.owner)) {
    abandon(record);
    return;
  }
  // Retire temporary listeners before our own focusin. No stale callback can win.
  abandon(record);
  target.focus({ preventScroll: true });
}

function trackMutation(event) {
  // A retained owner also observes its own event when detached. Connected events
  // reach window first; processing either path must track the promise only once.
  if (seenMutations.has(event)) return;
  seenMutations.add(event);
  const candidate = activation;
  activation = null;
  const overlap = requests.size > 0 || Boolean(current);
  abandon();
  consumeQuantity(event);
  // Snapshot origin BEFORE a component's own morph can detach the emitter.
  // Only listeners on the actual document event path can launch fallback renders.
  const fallbackSections = event.composedPath().includes(document)
    ? [...document.querySelectorAll('cart-items-component')].filter(cartPage).filter((owner) =>
        event.type === StandardEvents.cartLinesUpdate ? event.target !== owner :
          !(event.target instanceof Node) || !owner.contains(event.target)
      ).map((owner) => owner.dataset.sectionId)
    : [];
  const request = event.promise;
  if (!(request instanceof Promise)) {
    uncertainMutation = true;
    return;
  }
  const token = {};
  requests.add(token);
  const matches = !overlap && !uncertainMutation && !quantityWatches.size && candidate &&
    event instanceof CartLinesUpdateEvent && event.target === candidate.owner && event.context === 'cart' &&
    event.action === 'remove' && event.lines?.length === 1 && event.lines[0].id === candidate.key &&
    event.lines[0].quantity === 0 && candidate.click.eventPhase !== Event.NONE &&
    !candidate.click.defaultPrevented && document.activeElement === candidate.button &&
    candidate.row.isConnected && candidate.row.dataset.key === candidate.key &&
    candidate.button.closest(ROW) === candidate.row && candidate.row.closest('cart-items-component') === candidate.owner;
  let record = null;
  if (matches) {
    record = candidate;
    record.request = request;
    record.listeners = new AbortController();
    current = record;
    watchIntent(record);
    if (!contextValid(record) || record.transitionBefore || viewTransition.current) abandon(record);
    if (record.empty) {
      // The global current promise has no owner/type identity. Idle-before plus
      // populated-after is NOT proof that a native transition belongs to us.
      // Deliberately abstain on native/ambiguous paths without patching core.
      if (!record.synchronousFallback || !synchronousEmptyFallback()) abandon(record);
      queueMicrotask(() => {
        if (!isCurrent(record)) return;
        // This nested dispatch's microtask runs after core continues and replaces
        // children synchronously. A missing template or native transition cancels.
        if (!contextValid(record) || viewTransition.current || ownedRows(record.owner).length ||
            !cartPage(record.owner)?.classList.contains('cart-page--empty')) abandon(record);
      });
    }
  }
  // Attach success AND rejection handlers now, including non-qualifying requests.
  request.then((result) => {
    requests.delete(token);
    hasPendingQuantity();
    // External updates without bundled sections can launch an unobservable
    // renderSection request. Suppress later recovery until a document reload.
    if (fallbackSections.some((id) => !result?.detail?.sections?.[id])) uncertainMutation = true;
    if (!record || !isCurrent(record)) return;
    if (!contextValid(record) || !validResult(record, result)) {
      abandon(record);
      return;
    }
    // Core resolves immediately before its synchronous full morph; this reaction
    // runs after that morph. Exactly one frame, with fresh DOM checks in recover.
    requestAnimationFrame(() => {
      if (isCurrent(record)) recover(record, result);
    });
  }, () => {
    requests.delete(token);
    hasPendingQuantity();
    if (record && isCurrent(record)) abandon(record);
  }).catch(() => {
    // Also contain validation/DOM exceptions; never retire a newer operation.
    if (record && isCurrent(record)) abandon(record);
  });
}

window.addEventListener('pointerdown', (event) => {
  hasPendingQuantity();
  const button = removeButton(event.target);
  if (button) pointerButtons.add(button);
  activation = null;
}, true);
window.addEventListener('keydown', (event) => {
  hasPendingQuantity();
  const button = removeButton(event.target);
  if (event.isTrusted && !event.repeat && !event.isComposing && !event.ctrlKey && !event.altKey && !event.metaKey &&
      (event.key === 'Enter' || event.key === ' ') && document.activeElement === button) pointerButtons.delete(button);
}, true);
// Window capture precedes Horizon's document-capture on:click dispatcher.
window.addEventListener('click', rememberActivation, true);
// Native dispatch may checkpoint microtasks between listeners: do NOT expire the
// activation in a capture-click microtask. eventPhase guards its synchronous use.
window.addEventListener('click', () => { activation = null; removeClick = null; });
// Document capture intentionally follows Cart Enter's window-level duplicate
// suppression: a stopped duplicate must not become a phantom pending quantity.
document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  abandon();
  activation = null;
  // A non-bubbling descendant event cannot reach core's document listener.
  if (!event.bubbles && event.target !== document) return;
  hasPendingQuantity();
  // Connected owners receive the same stream and supersede their one debounce.
  // Detached owners do not: keep those generations until their source leaves
  // the owner or their retained callback emits its independently tracked request.
  for (const intent of quantityWatches.values()) {
    if (intent.owner.isConnected) retireQuantity(intent);
  }
  if (!(event instanceof QuantitySelectorUpdateEvent)) return;
  const selector = event.target;
  const owner = selector instanceof Element ? selector.closest('cart-items-component') : null;
  const line = event.detail?.cartLine;
  if (!owner || !owner.isConnected || !line) return;
  // Core addresses the row by cartLine when its debounce fires, not by selector.
  const row = ownedRows(owner)[line - 1];
  const intent = {
    generation: ++quantityGeneration, source: selector, owner, line,
    key: row?.dataset.key ?? '', quantity: event.detail.quantity, listeners: new AbortController(),
  };
  quantityWatches.set(owner, intent);
  owner.addEventListener(StandardEvents.cartLinesUpdate, trackMutation, { capture: true, signal: intent.listeners.signal });
  // Handles synchronous source removal after this nested quantity dispatch. Later
  // removals are pruned at interaction/request/eligibility boundaries, without a
  // timer or observer. A stale generation cannot prune a replacement intent.
  queueMicrotask(() => {
    if (quantityWatches.get(owner) === intent && !owner.contains(selector)) retireQuantity(intent);
  });
}, true);
for (const name of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate, StandardEvents.cartNoteUpdate]) {
  window.addEventListener(name, trackMutation, true);
}
window.addEventListener(StandardEvents.cartError, () => { activation = null; abandon(); }, true);
